/**
 * Service Dashboard (PR-KPI-SNAPSHOT Fase 5).
 *
 * I tre KPI statici (pazienti attivi, da ricontattare, compleanni) non
 * si calcolano più qui: arrivano dallo snapshot notturno. Resta solo
 * la Query A ristretta a oggi, che alimenta i due KPI dinamici
 * «Appuntamenti oggi» e «Prossimi appuntamenti oggi».
 *
 * `deriveAppuntamentiOggi` filtra già a [oggi 00:00, domani 00:00)
 * Europe/Rome: restringere la query a quella stessa finestra non cambia
 * i risultati, toglie solo i documenti che la derivazione scartava.
 *
 * Query C e il fan-out `fetchUltimiPassatiPerPaziente` non esistono più
 * in questo file. Le derivazioni statiche vivono in
 * `@shared/dashboard/kpi-snapshot` e le usa la Cloud Function.
 */

import {
  collectionGroup,
  getDocs,
  orderBy,
  query,
  where,
  type Query,
} from 'firebase/firestore';
import { firestore } from '../security/firebase';
import { vincoloVisibilitaAppuntamenti } from './visibilita';
import { getTodayRangeRome } from '@shared/utils/date';
import type { Appuntamento } from '@shared/types/appuntamento';
import type { AppuntamentoRecord } from './agenda';

export type { AppuntamentoRecord };

/**
 * Filtra gli appuntamenti non cancellati che cadono nel range di oggi.
 * Usato sia per il KPI count sia per la card "Prossimi appuntamenti oggi".
 */
export function deriveAppuntamentiOggi(
  appuntamenti: AppuntamentoRecord[],
  todayRange: { start: Date; end: Date },
): AppuntamentoRecord[] {
  const startIso = todayRange.start.toISOString();
  const endIso = todayRange.end.toISOString();
  return appuntamenti
    .filter(
      (a) =>
        a.stato !== 'cancellato' &&
        String(a.data) >= startIso &&
        String(a.data) < endIso,
    )
    .sort((a, b) => String(a.data).localeCompare(String(b.data)));
}

/**
 * Query A: appuntamenti di oggi [00:00, 24:00) Europe/Rome.
 *
 * PR-E — own-only DIETOLOGA: `where('dietologaId','==',scopeUid)`
 * server-side. Senza quel where la collectionGroup unscoped viene
 * rifiutata. Indice: (dietologaId ASC, data ASC) collection-group, già in
 * firestore.indexes.json.
 *
 * `scopeUid` NON è necessariamente l'uid del chiamante: per un account di
 * backoffice con delega è l'uid delegante (PR-BO-DELEGA). Arriva già
 * risolto da `useAuth.dietologaScopeUid`, che è la stessa cosa che
 * `dietologaScopeUid()` calcola nelle Rules.
 */
export async function fetchDashboardAppuntamenti(
  role: string,
  scopeUid: string,
  now: Date,
  isBackoffice: boolean,
): Promise<AppuntamentoRecord[]> {
  const todayRange = getTodayRangeRome(now);
  const startIso = todayRange.start.toISOString();
  const endIso = todayRange.end.toISOString();

  const constraints = [
    ...vincoloVisibilitaAppuntamenti(isBackoffice),
    ...(role === 'DIETOLOGA' ? [where('dietologaId', '==', scopeUid)] : []),
    where('data', '>=', startIso),
    where('data', '<', endIso),
    orderBy('data', 'asc'),
  ];
  const q = query(
    collectionGroup(firestore, 'appuntamenti'),
    ...constraints,
  ) as Query;
  const snap = await getDocs(q);

  return snap.docs.map((d) => ({
    id: d.id,
    pazienteId: d.ref.parent.parent?.id ?? '',
    ...(d.data() as Appuntamento),
  }));
}
